import {
  BiCardChange,
  BiCardChangeField,
  BiChangeEntry,
  BiChangeKind,
} from '@euler/model/bi/change-set';
import {
  BiPlacement,
  BiPlacementEdge,
  BiPlacementSize,
} from '@euler/model/bi/dashboard';
import {
  BiRelativeTimePreset,
  BiTimeGrain,
  BiTimeRangeKind,
  biRollingTimeRangeOfText,
} from '@euler/model/bi/query';
import {
  BiPalette,
  BiVizType,
  kBiPaletteNames,
  kBiVizStyleOptionNames,
} from '@euler/model/bi/viz';
import {
  biTimeRangeLabel,
  kBiCardOwnRangeLabel as kOwn,
} from '../../bi-visualization/functions/biTimeRangeLabel';
import {
  kBiGrainLabels,
  kBiVizTypeLabels,
} from '../../bi-visualization/functions/biVizLabels';

const kSizeLabels: Record<BiPlacementSize, string> = {
  [BiPlacementSize.Small]: '小尺寸',
  [BiPlacementSize.Medium]: '中尺寸',
  [BiPlacementSize.Large]: '大尺寸',
  [BiPlacementSize.Full]: '通栏',
};

type Value = string | number | null | undefined;

/**
 * A preset, `from~to` or a `rolling:<unit>:<count>:<current|whole>`
 * token, as the dashboard's date bar words it.
 */
function rangeText(value: Value, none: string): string {
  if (value == null) return none;
  const text = String(value);
  const rolling = biRollingTimeRangeOfText(text);
  if (rolling) return biTimeRangeLabel(rolling);
  const [from, to] = text.split('~');
  return to
    ? biTimeRangeLabel({ kind: BiTimeRangeKind.Absolute, from, to })
    : biTimeRangeLabel({
        kind: BiTimeRangeKind.Relative,
        preset: text as BiRelativeTimePreset,
      });
}

function grainText(value: Value): string {
  return value == null
    ? '不分时间'
    : kBiGrainLabels[value as BiTimeGrain] ?? String(value);
}

function paletteText(value: Value): string {
  return value == null
    ? '默认'
    : kBiPaletteNames[value as BiPalette] ?? String(value);
}

/** A style option's value; null is the theme's (a palette: the dashboard's). */
function styleText(change: BiCardChange, value: Value): string {
  if (value != null) return String(value);
  return change.option === 'palette' ? '跟随看板' : '默认';
}

function vizText(value: Value): string {
  return kBiVizTypeLabels[value as BiVizType] ?? String(value ?? '');
}

const kAdjusted: Partial<Record<BiCardChangeField, string>> = {
  [BiCardChangeField.Dataset]: '换了数据集',
  [BiCardChangeField.Measures]: '调整指标',
  [BiCardChangeField.Dimensions]: '调整维度',
  [BiCardChangeField.Filters]: '调整筛选条件',
  [BiCardChangeField.Sort]: '调整排序',
  [BiCardChangeField.Viz]: '调整图表样式',
  [BiCardChangeField.Blend]: '调整数据组合',
};

/** One field of a card update, e.g. `按月 → 按周`. */
export function biCardChangeText(change: BiCardChange): string {
  const { before, after } = change;
  switch (change.field) {
    case BiCardChangeField.Title:
      return `标题「${before ?? ''}」→「${after ?? ''}」`;
    case BiCardChangeField.Grain:
      return `${grainText(before)} → ${grainText(after)}`;
    case BiCardChangeField.TimeRange:
      return `时间 ${rangeText(before, '不限')} → ${rangeText(after, '不限')}`;
    case BiCardChangeField.Limit:
      return after == null ? '不限条数' : `只显示前 ${after} 条`;
    case BiCardChangeField.VizType:
      return `${vizText(before)} → ${vizText(after)}`;
    case BiCardChangeField.Style: {
      const name = change.option
        ? kBiVizStyleOptionNames[change.option] ?? change.option
        : '样式';
      return `${name} ${styleText(change, before)} → ${styleText(
        change,
        after,
      )}`;
    }
    case BiCardChangeField.Series:
      return `系列 ${before ?? ''} → ${after ?? ''}`;
    case BiCardChangeField.Palette:
      return `配色 ${paletteText(before)} → ${paletteText(after)}`;
    default:
      return kAdjusted[change.field] ?? '调整设置';
  }
}

function placementText(
  placement: BiPlacement | undefined,
  titles: ReadonlyMap<string, string>,
): string {
  if (!placement) return '';
  const { anchor, size } = placement;
  const near = (id: string) => `「${titles.get(id) ?? '另一张图表'}」`;
  const where =
    anchor === BiPlacementEdge.Top
      ? '放到最上方'
      : anchor === BiPlacementEdge.Bottom
      ? '放到最下方'
      : 'before' in anchor
      ? `放到${near(anchor.before)}前面`
      : `放到${near(anchor.after)}后面`;
  return size ? `${where}，${kSizeLabels[size]}` : where;
}

/**
 * One line of a change set's diff in words. `titles` names the cards a
 * placement is relative to.
 */
export function biChangeEntryText(
  entry: BiChangeEntry,
  titles: ReadonlyMap<string, string>,
): string {
  const title = `「${entry.title}」`;
  const where = placementText(entry.placement, titles);
  switch (entry.kind) {
    case BiChangeKind.CardAdded:
      return where ? `添加${title}，${where}` : `添加${title}`;
    case BiChangeKind.CardRemoved:
      return `删除${title}`;
    case BiChangeKind.CardMoved:
      return where ? `把${title}${where}` : `移动${title}`;
    case BiChangeKind.CardUpdated:
      return entry.changes.length
        ? `${title}：${entry.changes.map(biCardChangeText).join('；')}`
        : `修改${title}`;
    case BiChangeKind.DashboardRenamed:
      return `看板改名为${title}`;
    case BiChangeKind.FiltersChanged: {
      const [range] = entry.changes;
      return range
        ? `看板日期范围 ${rangeText(range.before, kOwn)} → ${rangeText(
            range.after,
            kOwn,
          )}`
        : '调整看板日期范围';
    }
    case BiChangeKind.PaletteChanged: {
      const [palette] = entry.changes;
      return palette
        ? `看板配色 ${paletteText(palette.before)} → ${paletteText(
            palette.after,
          )}`
        : '调整看板配色';
    }
  }
}
